/**
 * PublicPackagePage — cold public entry to a form PACKAGE (Quiet Trust theme).
 * Route: /pkg/:locationId/:slug
 *
 * A package bundles several forms into one link. Public packages ALWAYS require
 * OTP (authMode ∈ otp_email | otp_phone — enforced server-side), so the flow is:
 *   1. GET /api/forms/packages/public/:locationId/:slug → package meta (names only)
 *   2. identity capture (email or phone per authMode) →
 *      POST /api/forms/packages/public/request-otp → 6-digit entry →
 *      POST /api/forms/packages/public/verify-otp → returns { grant, runId }
 *      (verify CREATES-OR-RESUMES the run server-side)
 *   3. render <PackageRunView runId grant> — the shared stepper drives the forms;
 *      submits carry the run grant, file uploads authenticate via x-run-grant.
 *   4. if the grant (or run) expires mid-run, bounce back to the OTP step — a
 *      fresh verify RESUMES the same run.
 *
 * Mirrors PublicFormPage's patient surface (navy secure banner, single-column
 * container, teal tokens) — it must NOT read like the staff app. The OtpEntry
 * component is copied from PublicFormPage because the public lane owns it; a
 * shared otp/OtpEntry.tsx extraction is flagged as follow-up.
 */

import React, { useEffect, useState, useCallback, useRef, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import { publicPackage, PublicPackageMeta, SubmitterInput } from '../../lib/api.js';
import {
  resolveIdentityConfig, otpChannelKey, identityRows,
  validateIdentityValues, buildSubmitter, labelForKey, type IdentityFieldKey,
} from '../../lib/public-package-flow.js';
import PackageRunView from '../portal/PackageRunView.js';

type Screen =
  | { type: 'loading' }
  | { type: 'error'; message: string }
  | { type: 'identity' }
  | { type: 'otp' }
  | { type: 'run'; runId: string; grant: string };

const RESEND_COOLDOWN = 60;

// Per-field input metadata for the identity screen (matches PublicFormPage).
// `dob` uses <input type="date"> (ISO YYYY-MM-DD, per the identity contract).
const INPUT_TYPE: Record<IdentityFieldKey, string> = {
  firstName: 'text', lastName: 'text', dob: 'date', email: 'email', phone: 'tel',
};
const AUTOCOMPLETE: Record<IdentityFieldKey, string> = {
  firstName: 'given-name', lastName: 'family-name', dob: 'bday', email: 'email', phone: 'tel',
};
const PLACEHOLDER: Partial<Record<IdentityFieldKey, string>> = {
  email: 'you@example.com', phone: '+1 (555) 000-0000',
};

export default function PublicPackagePage(): React.ReactElement {
  const { locationId, slug } = useParams<{ locationId: string; slug: string }>();
  const [meta,       setMeta]       = useState<PublicPackageMeta | null>(null);
  const [screen,     setScreen]     = useState<Screen>({ type: 'loading' });
  // Live identity inputs, keyed by field key (firstName/lastName/dob/email/phone).
  const [values,     setValues]     = useState<Record<string, string>>({});
  // Committed identity — built once at the identity-step transition, sent with
  // verify-otp so the server stamps the run's submitter_enc (source 'patient').
  const [submitter,  setSubmitter]  = useState<SubmitterInput>({});
  const [requesting, setRequesting] = useState(false);
  const [maskedContact, setMaskedContact] = useState('');
  const [identityError, setIdentityError] = useState<string | null>(null);
  const [expiredNote, setExpiredNote] = useState<string | null>(null);

  useEffect(() => {
    if (!locationId || !slug) {
      setScreen({ type: 'error', message: 'This link looks incomplete. Please check it and try again.' });
      return;
    }
    publicPackage.getMeta(locationId, slug).then((res) => {
      if (res.success && res.data) {
        setMeta(res.data);
        setScreen({ type: 'identity' });
      } else {
        setScreen({ type: 'error', message: res.error ?? 'This intake package could not be found.' });
      }
    }).catch(() => setScreen({ type: 'error', message: 'We could not load this intake package. Please check your connection and try again.' }));
  }, [locationId, slug]);

  // Public packages are always OTP (server-enforced). Config absent (old API
  // without SI) → the DEFAULT trio; [] → contact-field-only identity screen.
  const authMode = meta?.authMode ?? 'otp_phone';
  const config   = useMemo(() => resolveIdentityConfig(meta?.identityFields), [meta?.identityFields]);
  const rows     = useMemo(() => identityRows(authMode, config), [authMode, config]);
  const otpKey   = otpChannelKey(authMode) ?? 'phone';
  const maxDob   = new Date().toISOString().slice(0, 10);

  // The OTP contact value — the channel row on the identity screen doubles as it.
  const channelContact = (): string => (values[otpKey] ?? '').trim();

  // --- Identity step: validate configured fields, request OTP, advance --------
  const handleIdentitySubmit = useCallback(async () => {
    if (!meta || !locationId || !slug) return;
    setIdentityError(null);

    const err = validateIdentityValues(rows, values);
    if (err) { setIdentityError(err); return; }

    // Build the identity from the CONFIGURED fields only; held in state and sent
    // with verify-otp. On a resume (re-OTP after expiry) the server back-fills
    // the run's submitter_enc ONLY when it is still NULL, so re-sending is safe.
    setSubmitter(buildSubmitter(config, values));

    setRequesting(true);
    try {
      const res = await publicPackage.requestOtp({ locationId, slug, contact: channelContact() });
      if (res.success && res.data) {
        setMaskedContact(res.data.masked);
        setScreen({ type: 'otp' });
      } else {
        setIdentityError(res.error ?? 'We could not send a verification code. Please try again.');
      }
    } catch {
      setIdentityError('Network error — please check your connection and try again.');
    } finally {
      setRequesting(false);
    }
  }, [meta, locationId, slug, rows, config, values, otpKey]);

  // --- OTP verify: throws on failure so OtpEntry clears + shows inline error --
  const handleVerifyOtp = useCallback(async (code: string) => {
    if (!meta || !locationId || !slug) throw new Error('Package not ready');
    const res = await publicPackage.verifyOtp({
      locationId, slug, contact: channelContact(), otp: code,
      // Omitted when the package config is empty ([] → no identity step).
      submitter: Object.keys(submitter).length ? submitter : undefined,
    });
    if (res.success && res.data) {
      setExpiredNote(null);
      setScreen({ type: 'run', runId: res.data.runId, grant: res.data.grant });
    } else {
      throw new Error(res.error ?? 'That code was not correct. Please try again.');
    }
  }, [meta, locationId, slug, values, otpKey, submitter]);

  const handleResendOtp = useCallback(async () => {
    if (!meta || !locationId || !slug) return;
    await publicPackage.requestOtp({ locationId, slug, contact: channelContact() });
  }, [meta, locationId, slug, values, otpKey]);

  // Grant expired mid-run → back to OTP step; re-verify RESUMES the same run.
  const handleSessionExpired = useCallback(() => {
    setExpiredNote('Your secure session expired. Enter a new code to pick up where you left off.');
    setScreen({ type: 'otp' });
  }, []);

  // --------------------------------------------------------------------------
  // Render
  // --------------------------------------------------------------------------

  const banner = (
    <div style={styles.secureBanner}>
      <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="rgba(255,255,255,0.7)" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0 }}>
        <rect x="3" y="11" width="18" height="11" rx="2"/>
        <path d="M7 11V7a5 5 0 0 1 10 0v4"/>
      </svg>
      Secure &amp; Encrypted
    </div>
  );

  const trustFooter = (
    <div style={styles.trustFooter}>
      <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="#9e9eaa" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0 }}>
        <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/>
      </svg>
      Your information is encrypted end-to-end
    </div>
  );

  if (screen.type === 'loading') {
    return (
      <div className="ci-portal" style={styles.page}>
        {banner}
        <div style={styles.container}><p style={styles.muted}>Loading…</p></div>
      </div>
    );
  }

  if (screen.type === 'error') {
    return (
      <div className="ci-portal" style={styles.page}>
        {banner}
        <div style={styles.container}>
          <div className="ci-enter" style={styles.gateCard}>
            <span style={styles.errorIcon} aria-hidden="true">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#c0392b" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M10.29 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/>
                <line x1="12" y1="9" x2="12" y2="13"/>
                <line x1="12" y1="17" x2="12.01" y2="17"/>
              </svg>
            </span>
            <h2 style={styles.cardTitle}>Something went wrong</h2>
            <p style={styles.cardSub}>{screen.message}</p>
            <p style={styles.errorFooter}>If you think this is a mistake, contact the office directly.</p>
          </div>
        </div>
      </div>
    );
  }

  // The stepper owns its own full-screen surface (banner + completion screen).
  if (screen.type === 'run') {
    return (
      <PackageRunView
        runId={screen.runId}
        grant={screen.grant}
        onSessionExpired={handleSessionExpired}
      />
    );
  }

  return (
    <div className="ci-portal" style={styles.page}>
      {banner}
      <div style={styles.container}>
        {meta && (
          <div style={styles.titleBlock}>
            <h1 style={styles.formTitle}>{meta.name}</h1>
            {meta.description && <p style={styles.formDesc}>{meta.description}</p>}
          </div>
        )}

        {screen.type === 'identity' && meta && (
          <div className="ci-enter" style={styles.gateCard}>
            <h2 style={styles.cardTitle}>Verify your identity</h2>
            <p style={styles.cardSub}>
              {otpKey === 'email'
                ? 'Enter your details to receive a verification code by email.'
                : 'Enter your details to receive a verification code by text.'}
            </p>

            {meta.forms.length > 0 && (
              <div style={styles.previewBox}>
                <p style={styles.previewLabel}>{meta.forms.length} forms in this intake</p>
                <ol style={styles.previewList}>
                  {[...meta.forms].sort((a, b) => a.sortOrder - b.sortOrder).map((f, i) => (
                    <li key={`${f.name}-${i}`} style={styles.previewItem}>{f.name}</li>
                  ))}
                </ol>
              </div>
            )}

            {rows.map((row) => (
              <div key={row.key} style={styles.fieldGroup}>
                <label style={styles.label} htmlFor={`pp-${row.key}`}>
                  {labelForKey(row.key)}
                  {!row.required && <span style={styles.optional}> (optional)</span>}
                </label>
                <input
                  id={`pp-${row.key}`}
                  className="ci-focusable"
                  type={INPUT_TYPE[row.key]}
                  autoComplete={AUTOCOMPLETE[row.key]}
                  max={row.key === 'dob' ? maxDob : undefined}
                  style={styles.input}
                  value={values[row.key] ?? ''}
                  onChange={(e) => setValues((prev) => ({ ...prev, [row.key]: e.target.value }))}
                  placeholder={PLACEHOLDER[row.key] ?? ''}
                  onKeyDown={(e) => { if (e.key === 'Enter') void handleIdentitySubmit(); }}
                />
              </div>
            ))}
            <p style={styles.reassure}>We use this only to verify it&apos;s you. Your information is encrypted.</p>
            {identityError && <div style={styles.errorBox} role="alert">{identityError}</div>}
            <button
              style={{ ...styles.submitBtn, opacity: requesting ? 0.7 : 1 }}
              onClick={() => void handleIdentitySubmit()}
              disabled={requesting}
              aria-busy={requesting}
            >
              {requesting ? 'Sending…' : 'Send Verification Code'}
            </button>
          </div>
        )}

        {screen.type === 'otp' && (
          <>
            {expiredNote && <div style={styles.noticeBox} role="status">{expiredNote}</div>}
            <OtpEntry
              channelLabel={otpKey}
              masked={maskedContact}
              onVerify={handleVerifyOtp}
              onResend={handleResendOtp}
            />
          </>
        )}

        {trustFooter}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// OtpEntry — auth-agnostic 6-digit entry (visual pattern lifted from
// PublicFormPage/OTPVerifyScreen). Inlined here because the public package path
// is owned by this lane; a shared otp/OtpEntry.tsx extraction is flagged as
// follow-up.
// ---------------------------------------------------------------------------

function OtpEntry({
  onVerify,
  onResend,
  masked,
  channelLabel,
}: {
  onVerify: (code: string) => Promise<void>;
  onResend: () => Promise<void>;
  masked?: string;
  channelLabel: 'email' | 'phone';
}): React.ReactElement {
  const [digits,     setDigits]     = useState<string[]>(Array(6).fill(''));
  const [error,      setError]      = useState<string | null>(null);
  const [loading,    setLoading]    = useState(false);
  const [resendSecs, setResendSecs] = useState(RESEND_COOLDOWN);
  const [focusedIdx, setFocusedIdx] = useState<number | null>(null);
  const inputRefs = useRef<(HTMLInputElement | null)[]>([]);

  useEffect(() => {
    if (resendSecs <= 0) return;
    const t = setTimeout(() => setResendSecs((s) => s - 1), 1000);
    return () => clearTimeout(t);
  }, [resendSecs]);

  const submit = useCallback(async (code: string) => {
    setLoading(true);
    setError(null);
    try {
      await onVerify(code);
    } catch (err) {
      setError((err as Error).message || 'Invalid code. Please try again.');
      setDigits(Array(6).fill(''));
      inputRefs.current[0]?.focus();
    } finally {
      setLoading(false);
    }
  }, [onVerify]);

  const handleDigitChange = (index: number, value: string) => {
    const digit = value.replace(/\D/g, '').slice(-1);
    const next = [...digits];
    next[index] = digit;
    setDigits(next);
    if (digit && index < 5) inputRefs.current[index + 1]?.focus();
    if (digit && next.every((d) => d !== '')) void submit(next.join(''));
  };

  const handleKeyDown = (index: number, e: React.KeyboardEvent) => {
    if (e.key === 'Backspace' && !digits[index] && index > 0) {
      inputRefs.current[index - 1]?.focus();
    }
  };

  const handlePaste = (e: React.ClipboardEvent) => {
    e.preventDefault();
    const pasted = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, 6);
    if (!pasted) return;
    const next = [...digits];
    for (let i = 0; i < 6; i++) next[i] = pasted[i] ?? '';
    setDigits(next);
    const lastIndex = Math.min(pasted.length - 1, 5);
    inputRefs.current[lastIndex]?.focus();
    if (pasted.length === 6) void submit(pasted);
  };

  const handleSubmitClick = () => {
    const code = digits.join('');
    if (code.length !== 6) { setError('Please enter all 6 digits'); return; }
    void submit(code);
  };

  const handleResend = async () => {
    setResendSecs(RESEND_COOLDOWN);
    setError(null);
    try {
      await onResend();
    } catch {
      setError('Failed to send a new code. Please try again.');
      setResendSecs(0);
    }
  };

  return (
    <div className="ci-enter" style={otpStyles.card}>
      <div style={otpStyles.iconWrap}>
        <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="var(--ci-primary)" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
          <rect x="3" y="11" width="18" height="11" rx="2"/>
          <path d="M7 11V7a5 5 0 0 1 10 0v4"/>
        </svg>
      </div>

      <h1 style={otpStyles.title}>Verify Your Identity</h1>
      <p style={otpStyles.subtitle}>
        We sent a 6-digit code to your {channelLabel}.{' '}
        {masked ? <strong style={{ color: '#1a2744' }}>{masked}</strong> : null} Enter it below to continue.
      </p>

      <div style={otpStyles.digitRow} onPaste={handlePaste}>
        {digits.map((digit, i) => (
          <input
            key={i}
            ref={(el) => { inputRefs.current[i] = el; }}
            type="text"
            inputMode="numeric"
            maxLength={1}
            value={digit}
            className={`ci-digit-${i}`}
            style={{
              ...otpStyles.digitInput,
              ...(loading ? otpStyles.digitDisabled : {}),
              ...(digit ? otpStyles.digitFilled : {}),
              ...(focusedIdx === i ? otpStyles.digitFocused : {}),
            }}
            onChange={(e) => handleDigitChange(i, e.target.value)}
            onKeyDown={(e) => handleKeyDown(i, e)}
            onFocus={() => setFocusedIdx(i)}
            onBlur={() => setFocusedIdx(null)}
            disabled={loading}
            aria-label={`Digit ${i + 1} of 6`}
          />
        ))}
      </div>

      {error && <div style={otpStyles.error} role="alert">{error}</div>}

      <button
        style={{ ...otpStyles.submitBtn, opacity: loading ? 0.7 : 1 }}
        onClick={handleSubmitClick}
        disabled={loading}
        aria-busy={loading}
      >
        {loading ? 'Verifying…' : 'Continue'}
      </button>

      <div style={otpStyles.resendRow}>
        {resendSecs > 0 ? (
          <span style={otpStyles.resendWait}>Resend in {resendSecs}s</span>
        ) : (
          <button style={otpStyles.resendBtn} onClick={() => void handleResend()}>
            Send a new code
          </button>
        )}
      </div>

      <div style={otpStyles.secureNote}>
        <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="#9e9eaa" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0 }}>
          <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/>
        </svg>
        End-to-end encrypted
      </div>
    </div>
  );
}

const F = 'var(--ci-font)';

const styles: Record<string, React.CSSProperties> = {
  page:        { minHeight: '100vh', background: 'var(--ci-surface)', fontFamily: F },
  secureBanner: {
    background:     '#1a2744',
    color:          'rgba(255,255,255,0.82)',
    padding:        '9px 20px',
    fontSize:       12,
    textAlign:      'center',
    display:        'flex',
    alignItems:     'center',
    justifyContent: 'center',
    gap:            6,
    letterSpacing:  '0.01em',
    fontFamily:     F,
  },
  container:   { maxWidth: 720, margin: '0 auto', padding: '24px 16px 48px' },
  titleBlock:  { marginBottom: 20 },
  formTitle:   { fontSize: 22, fontWeight: 700, color: '#1a2744', margin: '0 0 8px', fontFamily: F },
  formDesc:    { fontSize: 15, color: 'var(--ci-text-muted)', lineHeight: 1.6, margin: 0, fontFamily: F },
  gateCard: {
    background:   '#fff',
    borderRadius: 16,
    border:       '1px solid var(--ci-border)',
    padding:      '28px 24px',
    maxWidth:     440,
    width:        '100%',
    margin:       '0 auto',
    textAlign:    'center',
    fontFamily:   F,
  },
  previewBox: {
    background:   'var(--ci-primary-light)',
    border:       '1px solid var(--ci-border)',
    borderRadius: 10,
    padding:      '12px 16px',
    margin:       '0 0 18px',
    textAlign:    'left',
  },
  previewLabel: { fontSize: 12, fontWeight: 600, color: 'var(--ci-primary)', margin: '0 0 8px', fontFamily: F, letterSpacing: '0.01em' },
  previewList:  { listStyle: 'decimal', margin: 0, padding: '0 0 0 18px', display: 'flex', flexDirection: 'column', gap: 4 },
  previewItem:  { fontSize: 13, color: 'var(--ci-text-body)', fontFamily: F, lineHeight: 1.4 },
  errorIcon:   { display: 'inline-flex', alignItems: 'center', justifyContent: 'center', marginBottom: 12 },
  cardTitle:   { fontSize: 18, fontWeight: 700, color: 'var(--ci-text)', margin: '0 0 8px', fontFamily: F },
  cardSub:     { fontSize: 14, color: 'var(--ci-text-muted)', lineHeight: 1.6, margin: '0 0 20px', fontFamily: F },
  errorFooter: { fontSize: 12, color: 'var(--ci-text-muted)', margin: '16px 0 0', fontFamily: F },
  reassure:    { fontSize: 13, color: 'var(--ci-text-muted)', margin: '0 0 14px', textAlign: 'left', lineHeight: 1.5, fontFamily: F },
  muted:       { color: 'var(--ci-text-muted)', fontSize: 14, fontFamily: F },
  fieldGroup:  { marginBottom: 14, textAlign: 'left' },
  label:       { display: 'block', fontSize: 13, fontWeight: 500, color: 'var(--ci-text-body)', marginBottom: 5, fontFamily: F },
  optional:    { color: 'var(--ci-text-muted)', fontWeight: 400 },
  input: {
    width:        '100%',
    minHeight:    46,
    padding:      '12px 12px',
    border:       '1px solid var(--ci-border)',
    borderRadius: 8,
    fontSize:     15,
    color:        'var(--ci-text)',
    boxSizing:    'border-box' as const,
    outline:      'none',
    fontFamily:   F,
  },
  errorBox: {
    background:   '#fef4f2',
    border:       '1px solid #f5c6bc',
    borderRadius: 8,
    padding:      '10px 14px',
    color:        '#c0392b',
    fontSize:     13,
    marginBottom: 12,
    textAlign:    'left',
    fontFamily:   F,
  },
  noticeBox: {
    background:   'var(--ci-primary-light)',
    border:       '1px solid var(--ci-border)',
    borderRadius: 8,
    padding:      '10px 14px',
    color:        'var(--ci-primary)',
    fontSize:     13,
    fontWeight:   500,
    marginBottom: 16,
    textAlign:    'center',
    fontFamily:   F,
  },
  submitBtn: {
    width:         '100%',
    minHeight:     46,
    padding:       '13px',
    background:    'var(--ci-primary)',
    color:         '#fff',
    border:        'none',
    borderRadius:  8,
    fontSize:      15,
    fontWeight:    600,
    cursor:        'pointer',
    marginTop:     4,
    fontFamily:    F,
    letterSpacing: '0.01em',
  },
  trustFooter: {
    display:        'flex',
    alignItems:     'center',
    justifyContent: 'center',
    gap:            5,
    marginTop:      24,
    fontSize:       12,
    color:          'var(--ci-text-muted)',
    fontFamily:     F,
  },
};

const otpStyles: Record<string, React.CSSProperties> = {
  card: {
    background:   '#ffffff',
    borderRadius: 16,
    padding:      '40px 32px',
    maxWidth:     440,
    width:        '100%',
    margin:       '0 auto',
    border:       '1px solid var(--ci-border)',
    textAlign:    'center',
    fontFamily:   F,
  },
  iconWrap: {
    width:          56, height: 56,
    borderRadius:   '50%',
    background:     'var(--ci-primary-light)',
    display:        'flex',
    alignItems:     'center',
    justifyContent: 'center',
    margin:         '0 auto 24px',
  },
  title:    { margin: '0 0 10px', fontSize: 22, fontWeight: 700, color: 'var(--ci-text)', fontFamily: F },
  subtitle: { margin: '0 0 28px', fontSize: 15, color: 'var(--ci-text-muted)', lineHeight: 1.65, fontFamily: F },
  digitRow: { display: 'flex', justifyContent: 'center', gap: 10, marginBottom: 20 },
  digitInput: {
    width:        46, height: 54,
    border:       '2px solid var(--ci-border)',
    borderRadius: 10,
    fontSize:     22,
    fontWeight:   700,
    textAlign:    'center',
    color:        'var(--ci-text)',
    outline:      'none',
    transition:   'border-color 0.15s, background 0.15s',
    fontFamily:   F,
  },
  digitFilled:   { borderColor: 'var(--ci-primary)', background: 'var(--ci-primary-light)' },
  digitFocused:  { borderColor: 'var(--ci-primary)', boxShadow: '0 0 0 3px var(--ci-primary-focus)', outline: 'none' },
  digitDisabled: { background: 'var(--ci-border-light)', color: 'var(--ci-text-muted)' },
  error: {
    background:   '#fef4f2',
    border:       '1px solid #f5c6bc',
    borderRadius: 8,
    padding:      '10px 16px',
    color:        '#c0392b',
    fontSize:     14,
    marginBottom: 16,
    textAlign:    'left',
    fontFamily:   F,
  },
  submitBtn: {
    width:        '100%',
    minHeight:    46,
    padding:      '13px',
    background:   'var(--ci-primary)',
    color:        '#fff',
    border:       'none',
    borderRadius: 8,
    fontSize:     15,
    fontWeight:   600,
    cursor:       'pointer',
    marginBottom: 16,
    fontFamily:   F,
    letterSpacing: '0.01em',
  },
  resendRow:  { marginBottom: 28 },
  resendWait: { fontSize: 13, color: 'var(--ci-text-muted)', fontFamily: F },
  resendBtn: {
    background:     'none',
    border:         'none',
    color:          'var(--ci-primary)',
    cursor:         'pointer',
    fontSize:       13,
    fontWeight:     500,
    textDecoration: 'underline',
    fontFamily:     F,
  },
  secureNote: {
    display:        'flex',
    alignItems:     'center',
    justifyContent: 'center',
    gap:            5,
    fontSize:       11,
    color:          'var(--ci-text-muted)',
    lineHeight:     1.5,
    fontFamily:     F,
  },
};
